import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Alert, 
  Button,
  Card,
  CardContent,
  Fade
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { paymentApi, PaymentTokenManager } from '../../services/paymentApi';
import './styles.scss';

export const PaymentSuccessRedirectPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'success' | 'error'>('checking');
  const [message, setMessage] = useState('Confirming your payment...');
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    // Extract token from URL or session storage
    const urlToken = searchParams.get('token');
    const sessionToken = PaymentTokenManager.getToken();
    const token = urlToken || sessionToken;

    if (!token) {
      setStatus('error');
      setMessage('Payment token not found. Please contact support if you completed payment.');
      return;
    }

    // Start checking payment status
    checkPaymentStatus(token);
  }, [searchParams]);

  const checkPaymentStatus = async (token: string) => {
    try {
      
      // Check payment status via our backend
      const result = await paymentApi.verifyPaymentToken(token);
      
      if (result.verified && result.payment) {
        
        // Store payment data for registration flow
        PaymentTokenManager.storePaymentData(result.payment);
        
        setStatus('success');
        setMessage('Payment confirmed! Redirecting to complete your registration...');
        
        // Start countdown and redirect
        startCountdownRedirect();
        
      } else if (result.status === 'pending') {
        setMessage('Payment is processing, please wait...');
        
        // Retry after 3 seconds
        setTimeout(() => checkPaymentStatus(token), 3000);
        
      } else {
        setStatus('error');
        setMessage(result.error || 'Payment verification failed. Please contact support.');
      }
      
    } catch (error: any) {
      setStatus('error');
      setMessage('Unable to verify payment status. Please contact support if you completed payment.');
    }
  };

  const startCountdownRedirect = () => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Redirect to registration with payment verified flag
          navigate('/registration?payment_verified=true&userType=Homeowner');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleManualRedirect = () => {
    navigate('/registration?payment_verified=true&userType=Homeowner');
  };

  const handleContactSupport = () => {
    window.open('mailto:jackie@homedesignguild.com?subject=Payment%20Verification%20Issue', '_blank');
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2, color: '#00A06D' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Confirming Payment
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1c1c1c' }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Next Steps:</strong><br/>
                You'll be redirected to complete your account setup in {countdown} seconds.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              size="large"
              onClick={handleManualRedirect}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Continue Now
            </Button>
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Error sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Payment Verification Issue
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {message}
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                If you completed your payment successfully, please contact our support team 
                and we'll help you complete your registration manually.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleContactSupport}
                sx={{ minWidth: 140 }}
              >
                Contact Support
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/registration')}
                sx={{ minWidth: 140 }}
              >
                Start Over
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box className="payment-success-redirect-page">
      <Box className="content-container">
        <Fade in={true} timeout={500}>
          <Card className="status-card" elevation={3}>
            <CardContent sx={{ p: 4 }}>
              {renderContent()}
            </CardContent>
          </Card>
        </Fade>
      </Box>
    </Box>
  );
};

